'use client';

import {getEnvConfig} from '@web-catalog/env';
import {ComponentProps, forwardRef} from 'react';

import {YandexBannerAdReservingSpace} from '@web-catalog/components/Ads/Yandex';
import {PreorderLink} from '@web-catalog/components/Featuring/parts/AppsListComponent/PreorderLink/PreorderLink';

import {AppType, NO_CATEGORY_CATEGORY, SelectionApp} from '@web-catalog/types';

import {chunk} from '@web-catalog/utils/helpers';

import {AppCard} from '@web-catalog/ui-kit/AppCard';
import {ArrowLink} from '@web-catalog/ui-kit/ArrowLink';
import {Scroller} from '@web-catalog/ui-kit/Scroller';

import {AppForAppCard} from '../../types';
import styles from './AppsCarousel.module.scss';

export type Props = {
  title: string;
  appType: AppType;
  apps: Array<AppForAppCard | SelectionApp>;
  showLinkToAll: boolean;
  testId?: string;
  onSwipe?: ComponentProps<typeof Scroller>['onSwipe'];
  featuringId?: string;
  redesignRating?: boolean;
  allowAds?: boolean;
};

const chunkSize = 3;
const adIndices = [6, 12];

export const AppsCarousel = forwardRef<HTMLElement, Props>(
  (
    {
      title,
      featuringId,
      apps,
      appType,
      showLinkToAll,
      testId,
      onSwipe,
      allowAds,
      redesignRating,
    },
    ref,
  ) => {
    if (!apps.length) {
      return null;
    }
    const chunks = chunk(apps, chunkSize);

    return (
      <section ref={ref} data-testid={testId}>
        <h2>{title}</h2>
        <Scroller onSwipe={onSwipe}>
          {chunks.map((chunk, chunkIndex) => (
            <div className={styles.appsChunk} key={chunkIndex}>
              {chunk.map((app, appIndex) => {
                const position = chunkIndex * chunkSize + appIndex;
                const shouldRenderAd =
                  allowAds && adIndices.includes(position + 1);

                const componentByType =
                  'preorderStatus' in app && app.preorderStatus ? (
                    <PreorderLink
                      featuringId={featuringId as string}
                      key={app.appId}
                      isEnablePreorderRedesign
                      {...app}
                      placement="mainPage"
                    />
                  ) : (
                    <AppCard
                      key={app.appId}
                      app={app as AppForAppCard}
                      position={position}
                      listName={title}
                      redesignRating={redesignRating}
                    />
                  );

                if (shouldRenderAd) {
                  return (
                    <YandexBannerAdReservingSpace
                      key={`ad-${position}`}
                      fallback={componentByType}
                      isEnabled={(config) =>
                        config.WEB_YANDEX_AD_APPS_LIST_ENABLED
                      }
                      getBlockId={() =>
                        getEnvConfig().YANDEX_ADS_APPS_LIST_BANNER_ID
                      }
                      placement="apps_list"
                      adType="sd_banner_catalog_apps"
                      bannerContainerClassName={styles.adPlaceholder}
                      position={position}
                    />
                  );
                }
                return componentByType;
              })}
            </div>
          ))}
        </Scroller>
        {showLinkToAll && (
          <ArrowLink
            href={
              appType === AppType.GAMES
                ? `/games/${NO_CATEGORY_CATEGORY}`
                : `/${NO_CATEGORY_CATEGORY}`
            }
            direction="to-right"
            className={styles.arrowLink}
          >
            {appType === AppType.GAMES ? 'Все игры' : 'Все приложения'}
          </ArrowLink>
        )}
      </section>
    );
  },
);

AppsCarousel.displayName = 'AppsCarousel';
